import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Contact, FileText, Loader2, Pencil, Plus, RefreshCw, Search, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  PanelTableCard, PanelTableHeader, PanelTableFrame, PanelTableHeadRow, PanelTableHead,
  PanelTableBody, PanelTableRow, PanelTableCell, PanelTableEmpty, PanelTableActions,
} from "@/components/ui/panel-table";
import {
  EntityAvatar, EntityStatPill, EntityInfoBlock, EntityDetailRow, EntityModalHero,
} from "@/components/companies/entity-ui";

const emptyForm = () => ({ ad: "", kod: "", status: 1 });

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return value;
  }
};

const CompaniesListPage = ({
  apiBase,
  isDark,
  textClass,
  labelClass,
  cardClass,
  inputClass,
  onRefresh,
  onOpenTranscriptSettings,
  onOpenCrmSettings,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchCompanies = useCallback(async (syncParent = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/companies`);
      setCompanies(res.data || []);
      if (syncParent) onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Firmalar yüklenemedi");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, onRefresh]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const filtered = useMemo(() => {
    let rows = companies;
    if (statusFilter === "active") rows = rows.filter((c) => c.status === 1);
    if (statusFilter === "passive") rows = rows.filter((c) => c.status !== 1);
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      rows = rows.filter((c) =>
        (c.ad || "").toLowerCase().includes(q) ||
        (c.kod || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [companies, searchTerm, statusFilter]);

  const stats = useMemo(() => ({
    total: companies.length,
    active: companies.filter((c) => c.status === 1).length,
    passive: companies.filter((c) => c.status !== 1).length,
  }), [companies]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ad: row.ad || "", kod: row.kod || "", status: row.status ?? 1 });
    setDetailOpen(false);
    setFormOpen(true);
  };

  const openDetail = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };

  const handleSave = async () => {
    if (!form.ad?.trim()) {
      toast.error("Firma adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${apiBase}/companies?id=${editing.id}`, form);
        toast.success("Firma güncellendi");
      } else {
        await axios.post(`${apiBase}/companies`, form);
        toast.success("Firma oluşturuldu");
      }
      setFormOpen(false);
      await fetchCompanies(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.ad}" firması silinecek, emin misiniz?`)) return;
    try {
      await axios.delete(`${apiBase}/companies?id=${row.id}`);
      toast.success("Firma silindi");
      setDetailOpen(false);
      await fetchCompanies(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const rowHover = isDark
    ? "hover:bg-violet-500/5 cursor-pointer group"
    : "hover:bg-violet-50/60 cursor-pointer group";

  const statusBadge = (status) => (
    status === 1 ? (
      <Badge className={isDark ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
        Aktif
      </Badge>
    ) : (
      <Badge className={isDark ? "bg-slate-500/15 text-slate-400 border-slate-500/30" : "bg-gray-100 text-gray-600 border-gray-200"}>
        Pasif
      </Badge>
    )
  );

  return (
    <>
      <PanelTableCard isDark={isDark} className={cardClass}>
        <PanelTableHeader
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          accent="purple"
          icon={Building2}
          title="Firma Yönetimi"
          description="Firma kayıtları, transkript ve CRM ayarları"
          toolbar={(
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={fetchCompanies} disabled={loading} className={isDark ? "border-slate-600" : ""}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button type="button" size="sm" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/20">
                <Plus className="w-4 h-4 mr-1" /> Yeni Firma
              </Button>
            </div>
          )}
        />

        <div className="mx-4 mt-3 flex flex-wrap gap-2">
          <EntityStatPill label="Toplam Firma" value={stats.total} tone="indigo" isDark={isDark} />
          <EntityStatPill label="Aktif" value={stats.active} tone="emerald" isDark={isDark} />
          <EntityStatPill label="Pasif" value={stats.passive} tone="violet" isDark={isDark} />
        </div>

        <div className={`mx-4 mb-3 mt-3 p-3 rounded-xl border grid grid-cols-1 md:grid-cols-12 gap-3 ${isDark ? "border-slate-700/80 bg-slate-900/40" : "border-indigo-100 bg-indigo-50/30"}`}>
          <div className="md:col-span-4">
            <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Durum</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`h-9 text-xs w-full ${inputClass}`}><SelectValue /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="passive">Pasif</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-8">
            <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Ara</label>
            <div className="relative">
              <Search className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? "text-indigo-400" : "text-indigo-500"}`} />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Firma adı veya kod..."
                className={`h-9 pl-9 ${inputClass}`}
              />
            </div>
          </div>
        </div>

        <PanelTableFrame isDark={isDark}>
          <PanelTableHeadRow isDark={isDark}>
            <PanelTableHead>Firma</PanelTableHead>
            <PanelTableHead>Kod</PanelTableHead>
            <PanelTableHead>Durum</PanelTableHead>
            <PanelTableHead>Kayıt Tarihi</PanelTableHead>
            <PanelTableHead className="text-right w-28">İşlem</PanelTableHead>
          </PanelTableHeadRow>
          <PanelTableBody>
            {loading ? (
              <PanelTableEmpty colSpan={5} labelClass={labelClass}>
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" />
              </PanelTableEmpty>
            ) : filtered.length === 0 ? (
              <PanelTableEmpty colSpan={5} labelClass={labelClass}>
                Firma bulunamadı — yeni ekleyin
              </PanelTableEmpty>
            ) : filtered.map((row) => (
              <PanelTableRow key={row.id} isDark={isDark} className={rowHover} onClick={() => openDetail(row)}>
                <PanelTableCell>
                  <div className="flex items-center gap-3">
                    <EntityAvatar name={row.ad} theme="indigo" icon={Building2} />
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${textClass} group-hover:text-indigo-500 transition-colors`}>{row.ad}</div>
                      <div className={`text-[11px] mt-0.5 ${labelClass}`}>ID #{row.id}</div>
                    </div>
                  </div>
                </PanelTableCell>
                <PanelTableCell>
                  <span className={`font-mono text-xs px-2 py-0.5 rounded-md ${isDark ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-gray-700"}`}>
                    {row.kod || "—"}
                  </span>
                </PanelTableCell>
                <PanelTableCell>{statusBadge(row.status)}</PanelTableCell>
                <PanelTableCell className={`text-xs ${labelClass}`}>{formatDate(row.created_at)}</PanelTableCell>
                <PanelTableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <PanelTableActions>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onOpenTranscriptSettings?.(row)}
                      className={isDark ? "text-emerald-400 hover:text-emerald-300" : "text-emerald-600"}
                      title="Transkript Ayarları"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onOpenCrmSettings?.(row)}
                      className={isDark ? "text-violet-400 hover:text-violet-300" : "text-violet-600"}
                      title="CRM Ayarları"
                    >
                      <Contact className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(row)} title="Düzenle">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {row.kod !== "spn" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(row)}
                        className={isDark ? "text-rose-400" : "text-rose-600"}
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </PanelTableActions>
                </PanelTableCell>
              </PanelTableRow>
            ))}
          </PanelTableBody>
        </PanelTableFrame>
      </PanelTableCard>

      {/* Detay modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`max-w-lg p-0 overflow-hidden border-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:hover:opacity-100 [&>button]:hover:bg-white/10 ${isDark ? "bg-slate-900 text-white" : "bg-white"}`}>
          {selected && (
            <>
              <EntityModalHero
                name={selected.ad}
                subtitle={selected.kod ? `Kod: ${selected.kod}` : "Firma kaydı"}
                theme="indigo"
                icon={Building2}
                badges={[
                  selected.status === 1 ? "Aktif" : "Pasif",
                  selected.kod === "spn" ? "Sistem firması" : null,
                  formatDate(selected.created_at),
                ].filter(Boolean)}
              />
              <div className="px-6 pt-4 pb-2 space-y-3">
                <EntityInfoBlock title="Firma Bilgileri" icon={Building2} tone="indigo" isDark={isDark}>
                  <EntityDetailRow label="Firma Adı" value={selected.ad} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Kod" value={selected.kod} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                  <EntityDetailRow label="Durum" value={selected.status === 1 ? "Aktif" : "Pasif"} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Kayıt Tarihi" value={formatDate(selected.created_at)} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>

                <EntityInfoBlock title="Entegrasyonlar" tone="emerald" isDark={isDark}>
                  <p className={`text-xs mb-2 ${labelClass}`}>Transkript ve müşteri CRM ayarlarını bu firmadan yönetin.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { onOpenTranscriptSettings?.(selected); setDetailOpen(false); }}
                      className={isDark ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}
                    >
                      <FileText className="w-4 h-4 mr-1" /> Transkript
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { onOpenCrmSettings?.(selected); setDetailOpen(false); }}
                      className={isDark ? "border-violet-500/40 text-violet-300 hover:bg-violet-500/10" : "border-violet-200 text-violet-700 hover:bg-violet-50"}
                    >
                      <Contact className="w-4 h-4 mr-1" /> CRM Lead
                    </Button>
                  </div>
                </EntityInfoBlock>
              </div>
              <DialogFooter className={`px-6 py-4 border-t ${isDark ? "border-slate-800 bg-slate-950/50" : "border-gray-100 bg-gray-50/50"}`}>
                <Button type="button" variant="outline" onClick={() => setDetailOpen(false)} className={isDark ? "border-slate-600" : ""}>Kapat</Button>
                <Button type="button" variant="outline" onClick={() => openEdit(selected)} className={isDark ? "border-indigo-500/40 text-indigo-300" : "border-indigo-200 text-indigo-700"}>
                  <Pencil className="w-4 h-4 mr-1" /> Düzenle
                </Button>
                {selected.kod !== "spn" && (
                  <Button type="button" onClick={() => handleDelete(selected)} className="bg-rose-600 hover:bg-rose-500 text-white">
                    <Trash2 className="w-4 h-4 mr-1" /> Sil
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Form modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className={`max-w-md ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-indigo-500/15"><Building2 className="w-4 h-4 text-indigo-500" /></span>
              {editing ? "Firma Düzenle" : "Yeni Firma"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className={labelClass}>Firma Adı *</Label>
              <Input
                value={form.ad}
                onChange={(e) => setForm((f) => ({ ...f, ad: e.target.value }))}
                className={`mt-1 ${inputClass}`}
                placeholder="Örn: SPN"
              />
            </div>
            <div>
              <Label className={labelClass}>Kod</Label>
              <Input
                value={form.kod}
                onChange={(e) => setForm((f) => ({ ...f, kod: e.target.value }))}
                className={`mt-1 ${inputClass}`}
                placeholder="Örn: spn"
                disabled={!!editing}
              />
            </div>
            <div>
              <Label className={labelClass}>Durum</Label>
              <Select value={String(form.status)} onValueChange={(v) => setForm((f) => ({ ...f, status: parseInt(v, 10) }))}>
                <SelectTrigger className={`mt-1 ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="1">Aktif</SelectItem>
                  <SelectItem value="0">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>İptal</Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500 text-white">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompaniesListPage;
